import React, { useEffect, useMemo, useState } from 'react';

import AppBar from 'components/AppBar';
import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import ModalHeader from 'components/ModalHeader';
import { Desktop, Mobile } from 'components/Responsive';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { Airline } from 'models/airlineException';
import {
  PaymentExceptionProduct,
  PaymentExceptionProvider,
  PaymentExceptionRequest,
  PaymentExceptionResponse,
} from 'models/paymentException';
import { fetchAllAirlines } from 'actions/airlineException';
import { getPaymentExceptionProviders } from 'services/paymentExceptionService';
import { extractErrorMessage } from 'utils/generic';

import MultiSelectList, { MultiSelectOption } from './MultiSelectList';
import {
  DrawerContainer,
  DrawerBody,
  InfoCard,
  InfoCardTitle,
  InfoCardText,
  FieldGroup,
  FieldLabel,
  NativeSelect,
  SelectField,
  SelectFieldText,
  SelectFieldChevron,
  ToggleCard,
  FormFooter,
  SubmitButton,
} from './styles';

interface Props {
  show: boolean;
  companyId: string;
  lineOfCreditAvailable: boolean;
  initialValue?: PaymentExceptionResponse | null;
  onClose: () => void;
  onSubmit: (data: PaymentExceptionRequest) => Promise<void>;
}

const PRODUCT_OPTIONS: PaymentExceptionProduct[] = ['air', 'hotel', 'vehicle'];

const PaymentExceptionForm = ({ show, companyId, lineOfCreditAvailable, initialValue, onClose, onSubmit }: Props) => {
  const { t } = useStoreTranslation();
  const toast = useToast();

  const [product, setProduct] = useState<PaymentExceptionProduct | ''>('');
  const [providers, setProviders] = useState<string[]>([]);
  const [airlines, setAirlines] = useState<string[]>([]);
  const [creditCardByUser, setCreditCardByUser] = useState(false);
  const [productTouched, setProductTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [providerOptions, setProviderOptions] = useState<PaymentExceptionProvider[]>([]);
  const [providerOptionsLoading, setProviderOptionsLoading] = useState(false);

  const [airlineOptions, setAirlineOptions] = useState<Airline[]>([]);
  const [airlineOptionsLoading, setAirlineOptionsLoading] = useState(false);

  const [showProviderList, setShowProviderList] = useState(false);
  const [showAirlineList, setShowAirlineList] = useState(false);

  useEffect(() => {
    if (!show) return;
    setProduct(initialValue?.product || '');
    setProviders(initialValue?.provider ? initialValue.provider.split(',').map((p) => p.trim()).filter(Boolean) : []);
    setAirlines(initialValue?.airlines || []);
    setCreditCardByUser(initialValue?.creditCardByUser || false);
    setProductTouched(false);
    setProviderOptions([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, initialValue]);

  useEffect(() => {
    setProviderOptions([]);
    if (!show || !product) {
      return;
    }

    let cancelled = false;
    setProviderOptionsLoading(true);
    getPaymentExceptionProviders(product)
      .then((data) => {
        if (!cancelled) setProviderOptions(data);
      })
      .catch((error) => {
        if (!cancelled) {
          toast.add({ type: 'danger', message: extractErrorMessage(error) || t('defaultError.default') });
        }
      })
      .finally(() => {
        if (!cancelled) setProviderOptionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, show]);

  useEffect(() => {
    if (!show || product !== 'air' || airlineOptions.length > 0) return;
    setAirlineOptionsLoading(true);
    fetchAllAirlines()
      .then((response) => setAirlineOptions(response.data.data))
      .catch((error) => {
        toast.add({ type: 'danger', message: extractErrorMessage(error) || t('defaultError.default') });
      })
      .finally(() => setAirlineOptionsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, show]);

  const providerSelectOptions: MultiSelectOption[] = useMemo(() => {
    if (Array.isArray(providerOptions)) {
      return providerOptions.map((p) => ({ code: p.code, name: p.name }));
    }
    return [];
  }, [providerOptions]);

  const airlineSelectOptions: MultiSelectOption[] = useMemo(
    () => airlineOptions.map((a) => ({ code: a.id, name: a.name })),
    [airlineOptions],
  );

  const providerLabel = useMemo(() => {
    if (providers.length === 0) return '';
    return providers
      .map((code) => providerOptions.find((p) => p.code === code)?.name || code)
      .join(' / ');
  }, [providers, providerOptions]);

  const airlineLabel = useMemo(() => {
    if (airlines.length === 0) return '';
    return airlines.map((code) => airlineOptions.find((a) => a.id === code)?.name || code).join(' / ');
  }, [airlines, airlineOptions]);

  const productHasError = productTouched && !product;

  const handleProductChange = (value: string) => {
    setProduct(value as PaymentExceptionProduct);
    setProductTouched(true);
    setProviders([]);
    if (value !== 'air') setAirlines([]);
  };

  const handleSubmit = async () => {
    setProductTouched(true);
    if (!product) return;

    const data: PaymentExceptionRequest = {
      companyId,
      product,
      provider: providers.length > 0 ? providers.join(',') : null,
      airlines: product === 'air' ? airlines : [],
      creditCardByUser,
    };

    try {
      setSubmitting(true);
      await onSubmit(data);
    } finally {
      setSubmitting(false);
    }
  };

  const submitLabel = initialValue
    ? t('paymentException.form.submitEdit')
    : t('paymentException.form.submitAdd');

  const title = initialValue ? t('paymentException.form.titleEdit') : t('paymentException.form.titleAdd');

  return (
    <ApplicationOverlayContent sidebar show={show} onClose={onClose}>
      <DrawerContainer>
        <Mobile>
          <AppBar noGoBack rightIcon="/corporate/images/close.svg" rightAction={onClose}>
            {title}
          </AppBar>
        </Mobile>
        <Desktop>
          <ModalHeader title={title} onClose={onClose} />
        </Desktop>

        <DrawerBody>
          <InfoCard>
            <InfoCardTitle>{t('paymentException.form.infoTitle')}</InfoCardTitle>
            <InfoCardText>{t('paymentException.form.infoText')}</InfoCardText>
          </InfoCard>

          <FieldGroup>
            <FieldLabel>{t('paymentException.form.productLabel')}</FieldLabel>
            <NativeSelect
              $hasError={productHasError}
              value={product}
              onChange={(event) => handleProductChange(event.target.value)}
              onBlur={() => setProductTouched(true)}
            >
              <option value="">{t('paymentException.form.productPlaceholder')}</option>
              {PRODUCT_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {t(`paymentException.products.${option}`)}
                </option>
              ))}
            </NativeSelect>
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>{t('paymentException.form.providerLabel')}</FieldLabel>
            <SelectField
              type="button"
              $disabled={!product}
              disabled={!product}
              onClick={() => setShowProviderList(true)}
            >
              <SelectFieldText $placeholder={!providerLabel}>
                {providerLabel || t('paymentException.form.providerPlaceholder')}
              </SelectFieldText>
              <SelectFieldChevron src="/corporate/images/chevron.svg" alt="chevron" />
            </SelectField>
          </FieldGroup>

          {product === 'air' && (
            <FieldGroup>
              <FieldLabel>{t('paymentException.form.airlineLabel')}</FieldLabel>
              <SelectField type="button" onClick={() => setShowAirlineList(true)}>
                <SelectFieldText $placeholder={!airlineLabel}>
                  {airlineLabel || t('paymentException.form.airlinePlaceholder')}
                </SelectFieldText>
                <SelectFieldChevron src="/corporate/images/chevron.svg" alt="chevron" />
              </SelectField>
            </FieldGroup>
          )}

          <ToggleCard
            title={t('paymentException.form.creditCardByCompanyTitle')}
            message={t('paymentException.form.creditCardByCompanyMessage')}
            value={!creditCardByUser}
            onToggle={(_, value) => setCreditCardByUser(value ? false : true)}
            hide={!lineOfCreditAvailable}
          />

          <ToggleCard
            title={t('paymentException.form.creditCardByUserTitle')}
            message={t('paymentException.form.creditCardByUserMessage')}
            value={creditCardByUser}
            onToggle={(_, value) => setCreditCardByUser(value ? true : false)}
            hide={!lineOfCreditAvailable}
          />
        </DrawerBody>

        <FormFooter>
          <SubmitButton
            type="button"
            value={submitLabel}
            loading={submitting}
            disabled={submitting || !product}
            onClick={handleSubmit}
          />
        </FormFooter>

        <MultiSelectList
          show={showProviderList}
          title={t('paymentException.form.providerListTitle')}
          options={providerSelectOptions}
          initialSelected={providers}
          isLoading={providerOptionsLoading}
          onClose={() => setShowProviderList(false)}
          onConfirm={(values) => {
            setProviders(values);
            setShowProviderList(false);
          }}
        />

        <MultiSelectList
          show={showAirlineList}
          title={t('paymentException.form.airlineListTitle')}
          options={airlineSelectOptions}
          initialSelected={airlines}
          isLoading={airlineOptionsLoading}
          onClose={() => setShowAirlineList(false)}
          onConfirm={(values) => {
            setAirlines(values);
            setShowAirlineList(false);
          }}
        />
      </DrawerContainer>
    </ApplicationOverlayContent>
  );
};

export default PaymentExceptionForm;
